/**
 * The comms strip. Idle: one line (Systems: whether calls reach the desk and the plumbing is up, who is on duty in Dialpad, theme
 * and desk controls). Ringing: it grows
 * to the call cards (reps answer in the Dialpad app, so no Answer here). That growth is the desk's
 * one automatic motion. The words say whether calls reach the desk at all.
 */
import { Moon, Sun, SunMoon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { signOut, useActor } from '../../lib/auth';
import { dismissAfterCall, useLive } from '../../lib/live';
import { setPrefs, usePrefs, type Prefs } from '../../lib/prefs';
import { AfterCall } from './after-call';
import { CallCards, visibleCards } from './call-cards';
import { SystemsMenu } from './systems-menu';
import { Notifications } from './notifications';
import { OnNow } from './on-now';

const NEXT_THEME: Record<Prefs['theme'], Prefs['theme']> = { system: 'dark', dark: 'light', light: 'system' };

export function CommsStrip({ onToggleAssistant, assistantOpen }: { onToggleAssistant: () => void; assistantOpen: boolean }) {
  const { theme } = usePrefs();
  const actor = useActor();
  const live = useLive();
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : SunMoon;
  // Tick only while a card is on screen (a live call, or one that ended under 10 s ago).
  const now = useNow(visibleCards(live.cards, Date.now(), live.profileId).length > 0);
  const cards = visibleCards(live.cards, now, live.profileId);
  return (
    <>
      <header className="flex h-10 min-w-0 items-center gap-4 border-b border-line bg-surface px-4 text-body">
        {actor && <SystemsMenu status={live.status} calls={live.calls} />}
        <OnNow team={live.team} />
        <div className="ml-auto flex items-center gap-1">
          {actor && <Notifications />}
          <button
            type="button"
            onClick={() => {
              setPrefs({ theme: NEXT_THEME[theme] });
            }}
            className="grid size-8 place-items-center rounded-control text-ink-3 hover:bg-surface-2 hover:text-ink"
            aria-label={`Theme: ${theme}. Switch.`}
            title={`Theme: ${theme}`}
          >
            <ThemeIcon className="size-4" />
          </button>
          <button
            type="button"
            onClick={onToggleAssistant}
            className={`h-8 rounded-control px-2.5 text-hint ${assistantOpen ? 'bg-surface-2 text-ink' : 'text-ink-3 hover:bg-surface-2 hover:text-ink'}`}
          >
            Assistant <kbd className="ml-1">A</kbd>
          </button>
          {actor && (
            <span className="max-w-[200px] truncate px-1 text-hint text-ink-2" title={actor.email}>
              {actor.name ?? actor.email}
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              void signOut();
            }}
            className="h-8 rounded-control px-2.5 text-hint text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </header>
      <CallCards cards={cards} now={now} viewerProfileId={live.profileId} />
      <AfterCall prompt={live.afterCall} onDismiss={dismissAfterCall} />
    </>
  );
}

/** The current time, ticking each second while there is a call to time. */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!ticking) return;
    setNow(Date.now());
    const t = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(t);
    };
  }, [ticking]);
  return now;
}
